/**
 * MediaKeyControl - Control component for selecting a media key
 *
 * Handles both label and selector variants for media key controls.
 * Uses LayoutConfigContext to get and set media key values.
 * Always uses single-select mode (one media key per column).
 * Supports card variant for fullscreen/compare layouts.
 */

import {mediaCardTypeToKeys} from '@wandb/weave/common/types/media';
import {Select} from '@wandb/weave/components/Form/Select';
import {isNil} from 'lodash';
import React, {useCallback, useMemo} from 'react';
import {type SingleValue} from 'react-select';
import {twMerge} from 'tailwind-merge';

import {MetricSearchErrorNotice} from '../../MetricSearch/MetricSearchErrorNotice';
import {useIsNgwEnabledInWorkspace} from '../../MetricSearch/useIsNgwEnabledInWorkspace';
import {useMetricSearchState} from '../../MetricSearch/useMetricSearchState';
import {useDetectPanelVariant} from '../../Panel/hooks/hooks';
import {
  isResultLimitHintOption,
  MEDIA_KEY_RESULT_LIMIT,
  resultLimitHintOption,
} from '../../WorkspaceDrawer/Settings/media/components/mediaKeySelectShared';
import {useLayoutConfig} from '../layout/contexts/LayoutConfigContext';
import {
  SUPPORTED_MEDIA_DRAWER_TYPE_STRINGS,
  SUPPORTED_MEDIA_DRAWER_TYPES,
} from '../supportedMediaDrawerTypes';
import {useMediaKeyToTypeMap} from '../useGetMediaCardTypes';
import {isValidMediaKeyOptionForMetric} from '../utils/isValidMediaKeyOptionForMetric';
import {mediaCardTypeForMetric} from '../utils/mediaCardTypeForMetric';
import {getMediaTypeIcon} from '../utils/mediaTypeIcons';
import {CARD_DEFAULT_CLASSNAMES} from './cardStyles';
import {
  GenericSelectorDropdown,
  type SelectorOption,
} from './GenericSelectorDropdown';
import {Title} from './Title';
import {type MediaKeyControlProps} from './types';

export type {MediaKeyControlProps};

/**
 * Picks metric-search or static picker based on the NGW media picker gate, the
 * same one the settings drawer's media pickers use.
 */
export const MediaKeyControl: React.FC<MediaKeyControlProps> = props => {
  const shouldUseMetricSearch = useIsNgwEnabledInWorkspace();

  return shouldUseMetricSearch ? (
    <MediaKeyControlMetricSearch {...props} />
  ) : (
    <MediaKeyControlStatic {...props} />
  );
};

/**
 * NGW picker: options come from the metric search API
 */
const MediaKeyControlMetricSearch: React.FC<MediaKeyControlProps> = ({
  control,
  config,
  index,
  columnIndex,
}) => {
  const layoutConfig = useLayoutConfig();
  const panelMediaType = layoutConfig?.mediaType;

  const mediaMetricTypes = useMemo(
    () =>
      panelMediaType != null
        ? (mediaCardTypeToKeys(panelMediaType) as string[])
        : SUPPORTED_MEDIA_DRAWER_TYPE_STRINGS,
    [panelMediaType]
  );

  const {
    results,
    hasMore,
    isLoading,
    error,
    searchErrorMessage,
    retry,
    setSearch,
  } = useMetricSearchState({
    types: mediaMetricTypes,
    resultLimit: MEDIA_KEY_RESULT_LIMIT,
  });

  const selectedMediaKey = layoutConfig?.getMediaKey(columnIndex ?? 0);

  const options: SelectorOption<string>[] = useMemo(() => {
    const opts: SelectorOption<string>[] = [];
    let hasSelected = false;
    for (const metric of results) {
      // The server filters by `types` membership, which is wider than the card
      // the panel actually renders, so a result can match the query and still
      // not be offerable. Re-check per result, mirroring settings drawer picker.
      if (
        !isValidMediaKeyOptionForMetric(
          metric,
          panelMediaType,
          SUPPORTED_MEDIA_DRAWER_TYPES
        )
      ) {
        continue;
      }
      if (metric.key === selectedMediaKey) {
        hasSelected = true;
      }
      const cardType = mediaCardTypeForMetric(metric);
      opts.push({
        value: metric.key,
        label: metric.key,
        icon: cardType ? getMediaTypeIcon(cardType) : undefined,
      });
    }
    // Keep the current selection visible even when it's off the result page.
    if (selectedMediaKey != null && !hasSelected) {
      opts.unshift({value: selectedMediaKey, label: selectedMediaKey});
    }
    if (hasMore) {
      const hint = resultLimitHintOption(MEDIA_KEY_RESULT_LIMIT);
      opts.push({value: hint.value ?? '', label: hint.displayText});
    }
    return opts;
  }, [results, selectedMediaKey, panelMediaType, hasMore]);

  const isOptionDisabled = useCallback((option: SelectorOption<string>) => {
    return isResultLimitHintOption({
      value: option.value,
      displayText: option.label,
    });
  }, []);

  if (control.type === 'label') {
    return (
      <MediaKeyLabel
        control={control}
        config={config}
        index={index}
        columnIndex={columnIndex}
      />
    );
  }

  const metricSearchProps = {
    onInputChange: setSearch,
    filterOption: null,
    isLoading,
    isOptionDisabled,
  };

  return (
    <MediaKeyDropdown
      control={control}
      index={index}
      columnIndex={columnIndex}
      options={options}
      selectedMediaKey={selectedMediaKey}
      metricSearchProps={metricSearchProps}
      errorRow={
        <MetricSearchErrorNotice
          searchErrorMessage={searchErrorMessage}
          error={error}
          onRetry={retry}
        />
      }
    />
  );
};

/**
 * Legacy picker: options come from the in-memory media key list.
 */
const MediaKeyControlStatic: React.FC<MediaKeyControlProps> = ({
  control,
  config,
  index,
  columnIndex,
}) => {
  const layoutConfig = useLayoutConfig();

  // Get available media keys from LayoutConfigProvider
  // Passes columnIndex to get keys filtered by run and media type
  const availableMediaKeys = useMemo(
    () => layoutConfig?.getAvailableMediaKeys(columnIndex) ?? [],
    [layoutConfig, columnIndex]
  );

  // Get media keys with their types (sorted alphabetically for consistency)
  const mediaKeysWithTypes = useMediaKeyToTypeMap(availableMediaKeys);

  // Build options with icons (standard Select will ignore icons, card mode uses them)
  const options: SelectorOption<string>[] = useMemo(
    () =>
      mediaKeysWithTypes.map(({key, mediaType}) => ({
        value: key,
        label: key,
        icon: mediaType ? getMediaTypeIcon(mediaType) : undefined,
      })),
    [mediaKeysWithTypes]
  );

  // Get currently selected media key for this column
  const selectedMediaKey = useMemo(() => {
    return layoutConfig?.getMediaKey(columnIndex ?? 0);
  }, [layoutConfig, columnIndex]);

  if (availableMediaKeys.length === 0) {
    return null;
  }

  if (control.type === 'label') {
    return (
      <MediaKeyLabel
        control={control}
        config={config}
        index={index}
        columnIndex={columnIndex}
      />
    );
  }

  return (
    <MediaKeyDropdown
      control={control}
      index={index}
      columnIndex={columnIndex}
      options={options}
      selectedMediaKey={selectedMediaKey}
    />
  );
};

const MediaKeyLabel: React.FC<MediaKeyControlProps> = ({
  control,
  config,
  index,
  columnIndex,
}) => {
  const layoutConfig = useLayoutConfig();
  const defaultClassNames = twMerge(
    CARD_DEFAULT_CLASSNAMES,
    control.classNames
  );

  let mediaKey: string | undefined = undefined;
  if (columnIndex == null) {
    // when linked, there should be only 1 media key
    mediaKey = layoutConfig?.getMediaKeys()?.[0];
  } else {
    mediaKey = layoutConfig?.getMediaKey(columnIndex);
  }
  if (!mediaKey) {
    return null;
  }

  return (
    <Title
      key={`media-key-label-${index}`}
      label="Media key"
      value={mediaKey}
      className={defaultClassNames}
      labelPosition={control.labelPosition ?? 'inline'}
      showColon={config.mode === 'compare'}
      justifyContent={config.mode === 'compare' ? 'center' : 'start'}
    />
  );
};

type MediaKeyDropdownProps = {
  control: MediaKeyControlProps['control'];
  index: number;
  columnIndex?: number;
  options: SelectorOption<string>[];
  selectedMediaKey: string | undefined;
  /** Present only on the metric-search path */
  metricSearchProps?: {
    onInputChange: (value: string) => void;
    filterOption: null;
    isLoading: boolean;
    isOptionDisabled: (option: SelectorOption<string>) => boolean;
  };
  errorRow?: React.ReactNode;
};

const MediaKeyDropdown: React.FC<MediaKeyDropdownProps> = ({
  control,
  index,
  columnIndex,
  options,
  selectedMediaKey,
  metricSearchProps,
  errorRow = null,
}) => {
  const layoutConfig = useLayoutConfig();
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  // Parent container controls sizing.
  const defaultClassNames = twMerge(
    CARD_DEFAULT_CLASSNAMES,
    control.classNames
  );

  // Get selected option for display
  const selectedValue = useMemo(() => {
    if (!selectedMediaKey) {
      return null;
    }
    return options.find(opt => opt.value === selectedMediaKey) ?? null;
  }, [selectedMediaKey, options]);

  // Render function for card title
  const renderTitle = useCallback(() => {
    const displayValue = selectedValue?.value ?? selectedMediaKey ?? '';
    return (
      <Title
        className="px-4 pl-8 py-4"
        label="Media key"
        value={displayValue}
        labelPosition="separated"
      />
    );
  }, [selectedValue?.value, selectedMediaKey]);

  // Handler for selecting a media key
  const handleSelect = useCallback(
    (key: string | null) => {
      if (
        key != null &&
        isResultLimitHintOption({value: key, displayText: key})
      ) {
        return;
      }
      if (!layoutConfig || !key) {
        return;
      }
      if (isNil(columnIndex)) {
        layoutConfig.setMediaKeys([key]);
      } else {
        layoutConfig.setMediaKey(key, columnIndex);
      }
    },
    [layoutConfig, columnIndex]
  );

  // Handler for standard Select component (receives SelectorOption objects)
  const handleChange = useCallback(
    (value: SingleValue<SelectorOption<string>>) => {
      if (!value || !value.value || !layoutConfig) {
        return;
      }
      handleSelect(value.value);
    },
    [layoutConfig, handleSelect]
  );

  // Interactive dropdown (single-select only)
  const shouldUseCard = control.variant === 'card' || isFullScreenPanelPage;

  if (shouldUseCard) {
    return (
      <div
        className={defaultClassNames}
        data-test={`media-key-control-card-${index}`}>
        {errorRow}
        <GenericSelectorDropdown<string, false>
          size="small"
          selectedValue={selectedValue}
          onSelect={handleSelect}
          options={options}
          isClearable={true}
          isMulti={false}
          renderTitle={renderTitle}
          placeholder="Search media key..."
          menuItemClasses="text-sm/5"
          {...metricSearchProps}
        />
      </div>
    );
  }

  // Standard component (workspace view - direct dropdown)
  return (
    <div className={twMerge('mt-1 w-full', defaultClassNames)}>
      {errorRow}
      <Select<SelectorOption<string>, false>
        className="media-key-selector w-full"
        value={selectedValue}
        onChange={handleChange}
        options={options}
        placeholder="Select media key"
        size="medium"
        isSearchable={true}
        isClearable={false}
        isMulti={false}
        hideSelectedOptions={false}
        {...metricSearchProps}
      />
    </div>
  );
};
